/** @jsx jsx */
import { css, jsx } from "@emotion/core"

import React, { useEffect, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { IRecord } from "../types/schema"
import { NoResultsBlock } from "../Components/Tables/utils"
import { TableWrapper } from "../Components/TableWrapper"
import { BaseButton, Modal, Spacer, Table } from "@cd2/components"
import { PageWrapper } from "../Components/PageWrapper"
import { Toolbar } from "../Components/Toolbar"
import { ConfirmOnClick } from "../Components/ConfirmOnClick"
import { Form2 } from "@cd2/forms"
import { ConfirmCancelButtons } from "../Components/Buttons/ConfirmCancelButtons"
import { buttonConfigs } from "../constants/buttonConfigs"
import ReactPlayer from "react-player"
import { useHistory } from "react-router-dom"
import { brandColors, themeColors } from "../Components/EOSThemeProvider"
import { TextGroupMedium } from "@cd2/components/build/es6"
import { useManagedDraw } from "../hooks/useManagedDraw"

type IVideo = IRecord<"videos", "title" | "description" | "url" | "display_on_path">

export function SystemVideosIndexPage() {
  const skeem = window.skeem

  const showDrawer = useManagedDraw<false | IVideo, IVideo | null>({
    Component: ({ dialogData, onResolve, registerOutsideClick }) => {
      registerOutsideClick(() => onResolve(false))
      return (
        <VideoForm
          initialData={dialogData ?? {}}
          onSubmit={(formData: IVideo) => {
            onResolve(formData)
          }}
          onCancel={() => onResolve(false)}
        />
      )
    },
  })

  const { isPending, error, data, isFetching, refetch } = useQuery({
    queryKey: ["videos"],
    queryFn: async () => {
      const videos = await skeem.fetch2<
        IRecord<"videos", "title" | "description" | "url" | "display_on_path">[]
      >({
        model: {
          name: "videos",
          attributes: ["title", "description", "url", "display_on_path"],
          sort: {
            by: "title",
            direction: "asc",
          },
        },
      })
      return videos
    },
  })

  if (isPending) {
    return <div>Loading...</div>
  }

  if (error) {
    return <div>Error: {error.message}</div>
  }

  async function handleDelete(id: string) {
    await skeem.mutate({
      videos: {
        destroy: id,
      },
    })
    refetch()
  }

  async function handleCreate() {
    const video = await showDrawer(null)
    if (video) {
      await skeem.mutate({
        videos: {
          create: {
            title: video.title,
            description: video.description,
            url: video.url,
            display_on_path: video.display_on_path,
          },
        },
      })
      refetch()
    }
  }

  async function handleEdit(id: string) {
    const videoRecord = data?.find(v => v.id === id)
    if (videoRecord) {
      const video = await showDrawer(videoRecord)
      if (video) {
        await skeem.mutate({
          videos: {
            update: {
              id: videoRecord.id,
              title: video.title,
              description: video.description,
              url: video.url,
              display_on_path: video.display_on_path,
            },
          },
        })
        refetch()
      }
    }
  }

  return (
    <React.Fragment>
      <Toolbar
        items={[
          {
            onClick: handleCreate,
            children: "Create",
            buttonColor: brandColors.night,
            color: "white",
            iconColor: brandColors.mintPop,
          },
        ]}
      />
      <PageWrapper breadcrumbPath={["system", "videos"]} title="Videos">
        <TableWrapper
          status={
            isPending
              ? "loading"
              : isFetching
              ? "reloading"
              : data?.length === 0
              ? "noResults"
              : "done"
          }
          renderNoResults={() => <NoResultsBlock />}
          showTopScrollbar
        >
          <Table data={data}>
            <Table.DataColumn heading="Title" field="title" />
            <Table.DataColumn heading="Description" field="description" />
            <Table.DataColumn heading="URL" field="url" />
            <Table.DataColumn heading="Display On Path" field="display_on_path" />
            <Table.DataColumn
              css={css`
                width: 1px;
              `}
              field="id"
              render={val => <BaseButton onClick={() => handleEdit(val)}>Edit</BaseButton>}
            />
            <Table.DataColumn
              css={css`
                width: 120px;
              `}
              field="id"
              render={val => (
                <ConfirmOnClick
                  heading="Are you sure you want to Delete?"
                  onConfirm={() => handleDelete(val)}
                >
                  <BaseButton {...buttonConfigs.delete}>Delete</BaseButton>
                </ConfirmOnClick>
              )}
            />
          </Table>
        </TableWrapper>
      </PageWrapper>
    </React.Fragment>
  )
}

function VideoForm({
  initialData,
  onSubmit,
  onCancel,
}: {
  initialData?: any
  onSubmit: any
  onCancel: any
}) {
  return (
    <Form2.Form initialData={initialData} onSubmit={onSubmit}>
      <Form2.WithField<string>
        field="url"
        render={field => <VideoDisplay url={field.getValue()} />}
        subscribe
      />
      <Form2.Text field={"title"} label={"Title"} />
      <Form2.Text field={"description"} label={"Description"} />
      <Form2.Text field={"url"} label={"URL"} />
      <Form2.Text field={"display_on_path"} label={"Display On Path"} />
      <Form2.WithForm
        render={form => (
          <ConfirmCancelButtons
            onConfirm={() => {
              form.submit()
            }}
            onCancel={() => onCancel()}
          />
        )}
      />
    </Form2.Form>
  )
}

function VideoDisplay({ url }: { url: string }) {
  if (!url || url.trim().length === 0) {
    return null
  }
  return (
    <div
      css={css`
        position: relative;
        width: 100%;
        padding-bottom: 56.25%;
        aspect-ratio: 16/9;
        background: #f5f5f5;
      `}
    >
      {isValidYouTubeUrl(url) ? (
        <ReactPlayer
          url={url}
          controls={true}
          width="100%"
          height="100%"
          style={{ position: "absolute" }}
        />
      ) : (
        <div
          css={css`
            padding: 16px;
            position: absolute;
          `}
        >
          <p>
            Invalid YouTube URL: <br />
            {url}
          </p>
        </div>
      )}
    </div>
  )
}

function isValidYouTubeUrl(url: string): boolean {
  const youtubeRegex =
    /^(?:https?:\/\/)?(?:www\.)?(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))((\w|-){11})(?:\S+)?$/
  return youtubeRegex.test(url)
}

export function VideoWatcher() {
  const history = useHistory()
  const skeem = window.skeem

  const [isModalOpen, setIsModalOpen] = useState(false)

  const currentPath = history.location.pathname
    .split("/")
    .map(el => (isUUID(el) ? ":id" : el))
    .join("/")

  const {
    isPending,
    error,
    data: videos,
  } = useQuery({
    queryKey: ["videos"],
    queryFn: async () => {
      const video = await skeem.fetch2<
        IRecord<"videos", "title" | "description" | "url" | "display_on_path">[]
      >({
        model: {
          name: "videos",
          attributes: ["title", "description", "url", "display_on_path"],
        },
      })
      return video
    },
  })

  useEffect(() => {
    setIsModalOpen(false)
  }, [currentPath])

  const data = videos?.find(x => x.display_on_path === currentPath)

  if (isPending) {
    return null
  }

  if (error) {
    return null
  }

  if (!data) {
    return null
  }

  return (
    <React.Fragment>
      <Modal
        isOpen={isModalOpen}
        render={() => (
          <div>
            <VideoDisplay url={data.url} />
            <Spacer />
            <TextGroupMedium
              heading={data.title}
              body={data.description}
              theme={{ heading: { size: "small" } }}
            />
          </div>
        )}
        onRequestClose={() => setIsModalOpen(false)}
      />
      <div
        css={css`
          position: fixed;
          bottom: 60px;
          right: 10px;
          padding: 10px 16px;
          background-color: ${themeColors.darkestBlue};
          color: white;
          z-index: 999;
          border-radius: 20px;
          text-align: center;
          cursor: pointer;
          box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.5);
        `}
        onClick={() => setIsModalOpen(true)}
      >
        Video Tutorial
      </div>
    </React.Fragment>
  )
}

function isUUID(str: string) {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
  return uuidRegex.test(str)
}
